import React, { useState } from 'react';
import { useDebug } from '../context/DebugContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiTrash2, FiCopy, FiChevronUp, FiChevronDown } = FiIcons;

const DebugPanel = () => {
  const { debugMode, logs, clearLogs } = useDebug();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  if (!debugMode) return null;

  const getLogColor = (type) => {
    switch (type) {
      case 'error': return 'text-red-600 bg-red-50';
      case 'success': return 'text-green-600 bg-green-50';
      case 'request': return 'text-blue-600 bg-blue-50';
      case 'response': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(typeof text === 'object' ? JSON.stringify(text, null, 2) : text);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 neo-btn bg-neo-green hover:bg-green-300 border-2 border-black shadow-neo sm:w-auto w-full max-w-[200px] px-4 py-2 text-sm font-bold uppercase flex items-center gap-2"
      >
        <SafeIcon icon={FiChevronUp} />
        Debug Logs ({logs.length})
      </button>

      {isOpen && (
        <div className="fixed bottom-16 right-4 z-50 w-96 max-w-[90vw] sm:max-w-96 max-h-[80vh] flex flex-col neo-card bg-white border-2 border-black shadow-neo-xl">
          <div className="flex items-center justify-between p-4 border-b-2 border-black bg-gray-50">
            <h3 className="text-lg font-bold uppercase">Debug Panel</h3>
            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-gray-200 rounded">
              <SafeIcon icon={FiX} size={20} />
            </button>
          </div>

          <div className="p-4 border-b-2 border-black bg-gray-50">
            <div className="text-xs font-bold uppercase mb-2">Session Info</div>
            <div className="space-y-1 text-xs font-mono">
              <div className="flex justify-between">
                <span>User:</span>
                <span className="font-bold">{user ? user.email : 'Not logged in'}</span>
              </div>
              <div className="flex justify-between">
                <span>Token:</span>
                <span className="font-bold">{localStorage.getItem('access_token') ? 'Present' : 'None'}</span>
              </div>
              <div className="flex justify-between">
                <span>API URL:</span>
                <span className="font-bold truncate max-w-[200px]">{import.meta.env.VITE_API_URL}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-4 py-2 border-b-2 border-black bg-gray-50">
            <div className="text-xs font-bold uppercase">Logs ({logs.length})</div>
            <button onClick={clearLogs} className="neo-btn text-xs px-2 py-1 bg-red-100 hover:bg-red-200 border border-black flex items-center gap-1">
              <SafeIcon icon={FiTrash2} size={14} />
              Clear
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {logs.length === 0 ? (
              <div className="text-center text-gray-500 text-sm py-8">No logs yet</div>
            ) : (
              [...logs].reverse().map((log) => (
                <div key={log.id} className={`border border-black rounded ${getLogColor(log.type)}`}>
                  <div className="p-2 cursor-pointer flex items-start justify-between" onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-xs font-bold">
                        <span className="uppercase">{log.type}</span>
                        <span className="text-gray-400">{log.timestamp}</span>
                      </div>
                      <div className="text-xs mt-1">{log.message}</div>
                    </div>
                    <SafeIcon icon={expandedLog === log.id ? FiChevronDown : FiChevronUp} size={16} />
                  </div>
                  {expandedLog === log.id && log.data && (
                    <div className="border-t border-black p-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-bold uppercase">Data:</div>
                        <button onClick={() => copyToClipboard(log.data)} className="text-xs hover:underline flex items-center gap-1">
                          <SafeIcon icon={FiCopy} size={12} />
                          Copy
                        </button>
                      </div>
                      <pre className="text-xs font-mono overflow-x-auto p-2 bg-white border border-black rounded">
                        {typeof log.data === 'object' ? JSON.stringify(log.data, null, 2) : log.data}
                      </pre>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DebugPanel;
